import { FileText, Terminal, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface BotTechnicalCardProps {
  bot: {
    id: string;
    name: string;
    status: 'healthy' | 'warning' | 'error' | 'offline';
    uptime: number;
    memory: number;
    cpu: number;
    requests: number;
    version?: string;
    environment?: string;
  };
  onSelect: () => void;
  selected: boolean;
}

interface StatusDotProps {
  status: 'healthy' | 'warning' | 'error' | 'offline';
  pulse?: boolean;
}

function StatusDot({ status, pulse }: StatusDotProps) {
  const statusConfig = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    offline: 'bg-gray-500'
  };

  return (
    <div className={cn(
      'w-2 h-2 rounded-full',
      statusConfig[status],
      pulse && 'animate-pulse'
    )} />
  );
}

interface MetricItemProps {
  label: string;
  value: string;
  status?: 'success' | 'warning' | 'error';
}

function MetricItem({ label, value, status }: MetricItemProps) {
  const statusColors = {
    success: 'text-green-600',
    warning: 'text-yellow-600',
    error: 'text-red-600'
  };

  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn(
        'font-mono',
        status && statusColors[status]
      )}>
        {value}
      </span>
    </div>
  );
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

export function BotTechnicalCard({ bot, onSelect, selected }: BotTechnicalCardProps) {
  const viewLogs = (botId: string) => {
    console.log('Viewing logs for bot:', botId);
  };

  const openConsole = (botId: string) => {
    console.log('Opening console for bot:', botId);
  };

  const restartBot = (botId: string) => {
    console.log('Restarting bot:', botId);
  };

  const getEnvironmentBadge = (environment?: string) => {
    const variants = {
      production: 'default',
      staging: 'secondary',
      development: 'outline'
    };
    
    return environment ? variants[environment as keyof typeof variants] || 'outline' : 'outline';
  };

  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        selected && "ring-2 ring-primary"
      )}
      onClick={onSelect}
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-sm font-medium truncate">
              {bot.name}
            </CardTitle>
            {bot.version && (
              <div className="text-xs text-muted-foreground">
                {bot.version}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <StatusDot status={bot.status} pulse />
            {bot.environment && (
              <Badge variant={getEnvironmentBadge(bot.environment) as any} className="text-xs">
                {bot.environment}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Runtime Metrics */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <MetricItem
              label="Uptime"
              value={`${bot.uptime}%`}
              status={bot.uptime > 99 ? 'success' : bot.uptime > 95 ? 'warning' : 'error'}
            />
            <MetricItem
              label="Memory"
              value={`${bot.memory}MB`}
              status={bot.memory < 500 ? 'success' : bot.memory < 800 ? 'warning' : 'error'}
            />
            <MetricItem
              label="CPU"
              value={`${bot.cpu}%`}
              status={bot.cpu < 70 ? 'success' : bot.cpu < 90 ? 'warning' : 'error'}
            />
            <MetricItem
              label="Requests"
              value={formatNumber(bot.requests)}
            />
          </div>
          
          {/* Quick Actions */}
          <div className="flex gap-1 pt-2">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={(e) => {
                e.stopPropagation();
                viewLogs(bot.id);
              }}
              title="View Logs"
            >
              <FileText className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={(e) => {
                e.stopPropagation();
                openConsole(bot.id);
              }}
              title="Open Console"
            >
              <Terminal className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2"
              onClick={(e) => {
                e.stopPropagation();
                restartBot(bot.id);
              }}
              title="Restart Bot"
            >
              <RefreshCw className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}